import React, { useContext } from 'react';
import { Drawer, Skeleton } from 'antd';
import {
  BudgetCandidate,
  BudgetInstance,
  MyCreateBudgetForm,
} from './CreateBudgetForm';
import { merge } from 'lodash';
import {
  ContactsDocument,
  ContactsQuery,
  ContactsQueryVariables,
  GetAllClientsDocument,
  GetAllClientsQuery,
  GetAllClientsQueryVariables,
  GetBudgetDocument,
  useConfirmItineraryMutation,
  useContactsQuery,
  useCreateBudgetMutation,
  useGetAllClientsQuery,
  useGetBudgetConfigQuery,
  useGetBudgetsLazyQuery,
  useUpdateBudgetCustomMutation,
  useVehicleTypesQuery,
  VehicleTypesQuery,
} from '../../../graphql/datamodel.gen';
import { getClient } from '../../../apollo';
import { Client } from '../../clients/types';
import { BudgetConfig, Contact } from '../types';
import { budgetInstanceToJSON } from '../CreateItinerary/utils';
import { ApolloCache, NetworkStatus } from '@apollo/client';
import { MainContext } from '../../../contexts/mainContext';

export interface ICreateBudgetProps {
  budget?: BudgetInstance;
  refetchBudgets: () => any;
  refetchBudget?: () => any;
  visible: boolean;
  onClose: () => void;
  draft: boolean;
  loading?: boolean;
}

const defaultProps: ICreateBudgetProps = {
  visible: false,
  draft: false,
  loading: false,
  refetchBudgets: () => {},
  refetchBudget: () => {},
  onClose: () => {},
};

const CreateBudget: React.FC<ICreateBudgetProps> = (
  props: ICreateBudgetProps = defaultProps,
) => {
  const {
    loading: loadingClients,
    data: dataClients,
    error: errorClients,
    refetch: refetchClients,
    networkStatus: networkStatusClients,
  } = useGetAllClientsQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingBudgetConfig,
    data: dataBudgetConfig,
    error: errorBudgetConfig,
    refetch: refetchBudgetConfig,
    networkStatus: networkStatusBudgetConfig,
  } = useGetBudgetConfigQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingVehicleTypes,
    data: dataVehicleTypes,
    networkStatus: networkStatusVehicleTypes,
  } = useVehicleTypesQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    loading: loadingContacts,
    data: dataContacts,
    error: errorContacts,
    refetch: refetchContacts,
    networkStatus: networkStatusContacts,
  } = useContactsQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const getAllBudgetsLazyQuery = useGetBudgetsLazyQuery({
    client: getClient(),
    fetchPolicy: 'cache-and-network',
  });

  const [createBudget] = useCreateBudgetMutation({
    client: getClient(),
  });

  const [updateBudget] = useUpdateBudgetCustomMutation({
    client: getClient(),
  });

  const [confirmItineraryMut] = useConfirmItineraryMutation({
    client: getClient(),
  });

  const _loadingClients =
    loadingClients && networkStatusClients === NetworkStatus.setVariables;

  const _loadingBudgetConfig =
    (loadingBudgetConfig && !dataBudgetConfig) ||
    networkStatusBudgetConfig === NetworkStatus.refetch;

  const _loadingContacts =
    loadingContacts && networkStatusContacts === NetworkStatus.setVariables;

  const _loadingVehicleTypes =
    (loadingVehicleTypes && !dataVehicleTypes) ||
    networkStatusVehicleTypes === NetworkStatus.refetch;

  const loading =
    props.loading ||
    _loadingClients ||
    _loadingBudgetConfig ||
    _loadingContacts ||
    _loadingVehicleTypes;
  const clients = (dataClients?.clients || []) as Client[];
  const contacts = (dataContacts?.contacts || []) as Contact[];
  const vehicleTypes = (dataVehicleTypes?.vehicleTypes ||
    []) as VehicleTypesQuery['vehicleTypes'];
  const budgetConfig = dataBudgetConfig?.budgetConfig;

  const submitBudget = async (budgetCandidate: BudgetCandidate) => {
    const budgetJSON = budgetInstanceToJSON(budgetCandidate);

    budgetJSON.itineraries = budgetJSON.itineraries.map((itinerary) => {
      const { realized, ...itinerarysanitized } = itinerary;

      return {
        ...itinerarysanitized,
      };
    });

    const addClientToClientsListCache = (cache: ApolloCache<any>, client) => {
      cache.updateQuery<GetAllClientsQuery, GetAllClientsQueryVariables>(
        {
          query: GetAllClientsDocument,
        },
        (data) => {
          if (!data) {
            return null;
          }

          const existingClient =
            data?.clients?.find(
              (c) => c.volcanoClientId === client.volcanoClientId,
            ) || {};

          const clientToUpdate = merge({ ...existingClient }, { ...client });

          return {
            ...data,
            clients: [
              ...data.clients.filter(
                (c) => c.volcanoClientId !== client.volcanoClientId,
              ),
              clientToUpdate,
            ],
          };
        },
      );
    };

    if (!props.budget) {
      await createBudget({
        variables: {
          ...budgetJSON,
        },
        onCompleted: () => {
          getAllBudgetsLazyQuery[0]();
        },
        update: (cache, result) => {
          const client = result.data.createBudget?.client;

          if (client) {
            addClientToClientsListCache(cache, client);
          }
        },
      });
      props.onClose();
    } else {
      await updateBudget({
        variables: {
          id: props.budget.id,
          ...budgetJSON,
        },
        update: (cache, result) => {
          const client = result.data.updateBudget?.client;

          if (client) {
            if (client) {
              addClientToClientsListCache(cache, client);
            }
          }
        },
      });
      props.onClose();
    }
  };

  const confirmItinerary = async (itineraryId: string) => {
    await confirmItineraryMut({
      variables: {
        id: itineraryId,
      },
    });
    // await props.refetchBudget();
  };

  return (
    <CreateBudgetView
      draft={props.draft}
      refetchBudgets={props.refetchBudgets}
      refetchContacts={refetchContacts}
      refetchClients={refetchClients}
      onConfirmItinerary={confirmItinerary}
      budget={props.budget}
      onSubmit={submitBudget}
      loading={loading}
      visible={props.visible}
      onClose={props.onClose}
      clients={clients}
      contacts={contacts}
      vehicleTypes={vehicleTypes}
      budgetConfig={budgetConfig}
    />
  );
};

interface ICreateBudgetViewProps {
  draft: boolean;
  budget?: BudgetInstance;
  refetchBudgets: () => any;
  refetchContacts: () => any;
  refetchClients: () => any;
  visible: boolean;
  onClose: () => void;
  clients: Client[];
  contacts: Contact[];
  vehicleTypes: VehicleTypesQuery['vehicleTypes'];
  budgetConfig?: BudgetConfig;
  loading: boolean;
  onSubmit: (budgetCandidate: BudgetCandidate) => Promise<any>;
  onConfirmItinerary: (itineraryId: string) => Promise<any>;
}

const CreateBudgetView: React.FC<ICreateBudgetViewProps> = (
  props: ICreateBudgetViewProps,
) => {
  const { device } = useContext(MainContext);

  let width: string = '50%';

  if (device.type === 'desktop' && device.bigScreen) {
    width = '75%';
  }
  if (device.type === 'desktop' && !device.bigScreen) {
    width = '85%';
  }
  if (device.type === 'phone') {
    if (device.bigScreen) {
      width = '85%';
    } else {
      width = '100%';
    }
  }

  return (
    <Drawer
      title={
        props.budget
          ? `Gestionar presupuesto ${props.budget.readableId}`
          : 'Crear nuevo presupuesto'
      }
      width={width}
      visible={props.visible}
      destroyOnClose={true}
      forceRender={true}
      closable={true}
      maskClosable={false}
      onClose={props.onClose}
    >
      {props.loading && <Skeleton active={true} loading={true} />}
      {!props.loading && (
        <MyCreateBudgetForm
          draft={props.draft}
          refetchBudgets={props.refetchBudgets}
          refetchContacts={props.refetchContacts}
          refetchClients={props.refetchClients}
          onConfirmItinerary={props.onConfirmItinerary}
          budget={props.budget}
          close={props.onClose}
          onSubmit={props.onSubmit}
          allClients={props.clients}
          allContacts={props.contacts}
          allVehicleTypes={props.vehicleTypes}
          budgetConfig={props.budgetConfig}
        />
      )}
    </Drawer>
  );
};

export default CreateBudget;
